import React, { useCallback, useEffect, useState } from 'react';
import { eventAPI } from '../../lib/eventApi';
import { toast } from 'sonner';
import { UserPlus, Phone, EnvelopeSimple, Star, ArrowsClockwise, XCircle } from '@phosphor-icons/react';
import ConfirmDialog from '../ConfirmDialog';

// FindSubTab — list active Sub Pool members for this session with contact
// info, allow the active player to select one as their replacement, or
// submit an "Other / Not Listed" outside-sub request that goes to Admin/EM
// review.
//
// Access is gated by the BACKEND (admin / EM / active player / active sub);
// the frontend renders whatever the API returns. Logged-out / unrelated
// approved users get a 403 from GET /sub-pool and we show a clean error.
export default function FindSubTab({ sessionId, currentWeek, isAdminOrEM, myRegistrationRole }) {
  const [pool, setPool] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [otherOpen, setOtherOpen] = useState(false);
  const [otherForm, setOtherForm] = useState({
    name: '', phone: '', email: '', rating: '', notes: '',
  });
  const [error, setError] = useState(null);
  // In-app confirmation modal state (replaces window.confirm)
  const [pendingSelect, setPendingSelect] = useState(null);    // { sub }
  const [pendingCancel, setPendingCancel] = useState(null);    // { request }

  const load = useCallback(async () => {
    try {
      setLoading(true);
      // Load these independently — if sub_requests table is missing (e.g.
      // migration 010 not yet applied) we still want to show the Sub Pool.
      const poolPromise = eventAPI.getSubPool(sessionId);
      const reqPromise = eventAPI.listSubRequests(sessionId).catch((e) => {
        // 500 here usually means migration 010 hasn't been applied yet.
        console.warn('listSubRequests failed:', e?.response?.status, e?.message);
        return { data: { sub_requests: [] } };
      });
      const [poolRes, reqRes] = await Promise.all([poolPromise, reqPromise]);
      setPool(poolRes.data?.subs || []);
      setRequests(reqRes.data?.sub_requests || []);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not load Sub Pool');
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => { load(); }, [load]);

  // handleSelect now just opens the in-app confirmation modal — the
  // actual API call runs from `confirmSelect` once the player explicitly
  // confirms they've already spoken with the sub.
  const handleSelect = (sub) => {
    setPendingSelect({ sub });
  };

  const confirmSelect = async () => {
    const sub = pendingSelect?.sub;
    if (!sub) return;
    setBusyId(sub.registration_id);
    setPendingSelect(null);
    try {
      await eventAPI.createSubRequest(sessionId, {
        sub_registration_id: sub.registration_id,
        week_number: currentWeek ?? null,
      });
      toast.success(`Requested ${sub.name} as your sub`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not submit request');
    } finally {
      setBusyId(null);
    }
  };

  // handleCancel opens an in-app confirmation modal as well. We deliberately
  // keep this lighter-weight than the Select dialog — it's reversible
  // coordination, not contacting another player.
  const handleCancel = (req) => {
    setPendingCancel({ request: req });
  };

  const confirmCancel = async () => {
    const req = pendingCancel?.request;
    if (!req) return;
    setPendingCancel(null);
    try {
      await eventAPI.resolveSubRequest(sessionId, req.id, { status: 'cancelled' });
      toast.success('Request cancelled');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not cancel');
    }
  };

  const handleResolve = async (req, status) => {
    try {
      await eventAPI.resolveSubRequest(sessionId, req.id, { status });
      toast.success(`Request ${status}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update');
    }
  };

  const handleOtherSubmit = async (e) => {
    e.preventDefault();
    if (!otherForm.name.trim()) {
      toast.error('Name is required');
      return;
    }
    try {
      await eventAPI.createOtherSubRequest(sessionId, {
        name: otherForm.name.trim(),
        phone: otherForm.phone.trim() || null,
        email: otherForm.email.trim() || null,
        rating: otherForm.rating.trim() || null,
        notes: otherForm.notes.trim() || null,
        week_number: currentWeek ?? null,
      });
      toast.success('Outside sub request submitted — Admin/EM will review.');
      setOtherForm({ name: '', phone: '', email: '', rating: '', notes: '' });
      setOtherOpen(false);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not submit');
    }
  };

  if (loading) {
    return (
      <div className="space-y-3" data-testid="find-sub-loading">
        <div className="h-20 bg-[#f0ece3] rounded-2xl animate-pulse" />
        <div className="h-20 bg-[#f0ece3] rounded-2xl animate-pulse" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-2xl p-4 text-sm text-red-700" data-testid="find-sub-error">
        {error}
      </div>
    );
  }

  const myRequests = requests.filter((r) => r.status === 'pending' || r.status === 'approved');
  const isPlayer = myRegistrationRole === 'player';

  return (
    <div className="space-y-5" data-testid="find-sub-tab">
      <header className="space-y-1">
        <h2 className="font-serif text-2xl text-[#013523] tracking-tight">Subs / Alternates</h2>
        <p className="text-xs text-[#6a766e]">
          {isAdminOrEM
            ? 'You can review the Sub Pool and any pending sub requests below.'
            : 'Contact a Sub Pool member if you can\'t make the session. Tap a sub to request them as your replacement.'}
        </p>
      </header>

      {/* Current sub requests (for the caller). Admin/EM sees all. */}
      {requests.length > 0 && (
        <section className="bg-[#fbfaf8] border border-[#e3dcd1] rounded-2xl p-4 space-y-2" data-testid="find-sub-requests">
          <div className="text-[11px] uppercase tracking-wider text-[#6a766e]">
            {isAdminOrEM ? 'All Sub Requests' : 'My Sub Requests'}
          </div>
          <ul className="divide-y divide-[#e3dcd1]">
            {requests.map((r) => (
              <li key={r.id} className="py-2 flex items-start justify-between gap-2 text-sm" data-testid={`sub-request-${r.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-[#013523]">
                    {r.sub_player_name || r.other_name || '—'}
                    <span className="text-[#6a766e]"> subbing for </span>
                    <span className="text-[#013523] font-medium">{r.requesting_player_name || '—'}</span>
                  </div>
                  <div className="mt-0.5 text-[11px] text-[#6a766e] flex flex-wrap gap-x-3">
                    <span>Status: <span className={
                      r.status === 'approved' ? 'text-[#015e3f] font-medium'
                      : r.status === 'rejected' ? 'text-red-600'
                      : r.status === 'cancelled' ? 'text-[#6a766e]'
                      : 'text-amber-700 font-medium'
                    }>{r.status}</span></span>
                    {r.week_number != null && <span>Week {r.week_number}</span>}
                    {r.other_phone && <span>📞 {r.other_phone}</span>}
                    {r.other_email && <span>✉ {r.other_email}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {isAdminOrEM && r.status === 'pending' && (
                    <>
                      <button
                        onClick={() => handleResolve(r, 'approved')}
                        className="text-[11px] px-2 py-1 rounded-md bg-[#015e3f] text-white"
                        data-testid={`approve-sub-request-${r.id}`}
                      >Approve</button>
                      <button
                        onClick={() => handleResolve(r, 'rejected')}
                        className="text-[11px] px-2 py-1 rounded-md bg-white border border-[#e3dcd1] text-[#6a766e]"
                        data-testid={`reject-sub-request-${r.id}`}
                      >Reject</button>
                    </>
                  )}
                  {!isAdminOrEM && r.status === 'pending' && (
                    <button
                      onClick={() => handleCancel(r)}
                      className="text-[11px] px-2 py-1 rounded-md bg-white border border-[#e3dcd1] text-[#6a766e]"
                      data-testid={`cancel-sub-request-${r.id}`}
                    ><XCircle size={12} /> Cancel</button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Sub Pool list */}
      <section className="space-y-2" data-testid="find-sub-pool">
        <div className="flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-wider text-[#6a766e]">Sub Pool</div>
          <button
            onClick={load}
            className="text-[11px] text-[#015e3f] flex items-center gap-1"
            data-testid="refresh-sub-pool"
          >
            <ArrowsClockwise size={12} /> Refresh
          </button>
        </div>

        {pool.length === 0 ? (
          <div className="bg-[#fbfaf8] border border-[#e3dcd1] rounded-2xl p-4 text-sm text-[#6a766e]">
            No subs in the pool yet. {isAdminOrEM ? 'Add subs from the session editor.' : 'Try the "Other / Not Listed" option below.'}
          </div>
        ) : (
          <ul className="space-y-2">
            {pool.map((s) => (
              <li
                key={s.registration_id}
                className="bg-[#fbfaf8] border border-[#e3dcd1] rounded-2xl p-3 flex items-start justify-between gap-3"
                data-testid={`sub-pool-row-${s.registration_id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-[#013523] font-medium" data-testid={`sub-name-${s.registration_id}`}>{s.name}</div>
                  <div className="mt-0.5 grid gap-y-0.5 text-[11px] text-[#6a766e]">
                    <span className="flex items-center gap-1.5">
                      <EnvelopeSimple size={12} />
                      <span data-testid={`sub-email-${s.registration_id}`}>{s.email || <em className="text-[#a8a8a8]">Not provided</em>}</span>
                    </span>
                    <span className="flex items-center gap-1.5">
                      <Phone size={12} />
                      <span data-testid={`sub-phone-${s.registration_id}`}>{s.phone || <em className="text-[#a8a8a8]">Not provided</em>}</span>
                    </span>
                    <span className="flex items-center gap-1.5">
                      <Star size={12} />
                      <span data-testid={`sub-rating-${s.registration_id}`}>{s.rating || <em className="text-[#a8a8a8]">No rating</em>}</span>
                    </span>
                  </div>
                </div>
                {isPlayer && (
                  <button
                    onClick={() => handleSelect(s)}
                    disabled={busyId === s.registration_id}
                    className="text-[11px] px-2.5 py-1.5 rounded-md bg-[#015e3f] text-white disabled:opacity-60 shrink-0 flex items-center gap-1"
                    data-testid={`select-sub-btn-${s.registration_id}`}
                  >
                    <UserPlus size={12} /> {busyId === s.registration_id ? '…' : 'Select'}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Other / Not Listed — only for active players (not subs themselves) */}
      {isPlayer && (
        <section className="bg-[#fbfaf8] border border-[#e3dcd1] rounded-2xl p-4 space-y-2" data-testid="other-sub-section">
          <div className="flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-wider text-[#6a766e]">Other / Not Listed</div>
            {!otherOpen && (
              <button
                onClick={() => setOtherOpen(true)}
                className="text-[11px] text-[#015e3f] underline"
                data-testid="open-other-sub-btn"
              >
                Add an outside sub
              </button>
            )}
          </div>
          <p className="text-[11px] text-[#6a766e]">
            Found someone outside the club? Submit their info and Admin/EM will review.
          </p>
          {otherOpen && (
            <form onSubmit={handleOtherSubmit} className="space-y-2 mt-2" data-testid="other-sub-form">
              <Input label="Name *" value={otherForm.name} onChange={(v) => setOtherForm({ ...otherForm, name: v })} testid="other-sub-name" />
              <Input label="Phone" value={otherForm.phone} onChange={(v) => setOtherForm({ ...otherForm, phone: v })} testid="other-sub-phone" />
              <Input label="Email" value={otherForm.email} onChange={(v) => setOtherForm({ ...otherForm, email: v })} testid="other-sub-email" />
              <Input label="USTA rating (optional)" value={otherForm.rating} onChange={(v) => setOtherForm({ ...otherForm, rating: v })} testid="other-sub-rating" />
              <div className="flex gap-2 pt-1">
                <button type="submit" className="text-xs px-3 py-1.5 rounded-md bg-[#015e3f] text-white" data-testid="submit-other-sub-btn">
                  Submit for review
                </button>
                <button
                  type="button"
                  onClick={() => { setOtherOpen(false); setOtherForm({ name: '', phone: '', email: '', rating: '', notes: '' }); }}
                  className="text-xs px-3 py-1.5 rounded-md bg-white border border-[#e3dcd1] text-[#6a766e]"
                  data-testid="cancel-other-sub-btn"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </section>
      )}
      {/* In-app confirmation modal for sub selection. Replaces the prior
          window.confirm() call so players see context-specific copy
          (contact the sub first; this is coordination not assignment). */}
      <ConfirmDialog
        open={!!pendingSelect}
        title="Confirm Sub Selection"
        message={
          <span>
            Have you already confirmed with{' '}
            <span className="font-medium text-[#013523]">{pendingSelect?.sub?.name}</span>{' '}
            that they will play for you?
            <span className="block mt-2 text-xs text-[#6a766e]">
              Please contact the player first before selecting them as your sub.
              This does not automatically notify or force them to play.
            </span>
          </span>
        }
        confirmLabel="Yes, I Confirmed"
        cancelLabel="Cancel"
        onConfirm={confirmSelect}
        onCancel={() => setPendingSelect(null)}
        testId="confirm-sub-select"
      />

      {/* Lighter cancel-own-request confirm — also in-app. */}
      <ConfirmDialog
        open={!!pendingCancel}
        title="Cancel sub request?"
        message="This will withdraw your request to that sub. You can always submit a new one."
        confirmLabel="Cancel request"
        cancelLabel="Keep request"
        destructive
        onConfirm={confirmCancel}
        onCancel={() => setPendingCancel(null)}
        testId="confirm-sub-cancel"
      />
    </div>
  );
}

function Input({ label, value, onChange, testid }) {
  return (
    <label className="block text-xs text-[#6a766e]">
      <span className="block mb-0.5">{label}</span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full text-sm px-2 py-1.5 rounded-md border border-[#e3dcd1] bg-white text-[#013523] focus:outline-none focus:border-[#015e3f]"
        data-testid={testid}
      />
    </label>
  );
}
